import NavigationsPage from "@containers/NavigationsPage/NavigationPage"
import PageWrapper from "@components/Common/PageWrapper"
import styles from "./ScheduleAppointment.module.css"
import ArrowLeftIcon from "@assets/icons/arrowLeft"
import ConfirmAndSendIcon from "@assets/icons/confirmAndSend"
import MeetingInfoIcon from "@assets/icons/meetingInfo"
import NotesIcon from "@assets/icons/notes"
import PatientInfoIcon from "@assets/icons/patientInfo"
import ConfirmAndSend from "../../components/ScheduleAppointmentPage/ConfirmAndSend"
import ArrowSquareLeftIcon from "@assets/icons/arrowSquareLeft"
import Notes from "../../components/ScheduleAppointmentPage/Notes"
import MeetingInfo from "../../components/ScheduleAppointmentPage/MeetingInfo"
import PatientInfo from "../../components/ScheduleAppointmentPage/PatientInfo"
import { useState, useEffect } from "react"
import AppointmentSectionHeader from "@components/ScheduleAppointmentPage/AppointmentSectionHeader"
import { Link } from "react-router-dom"
import usePatient from "@hooks/usePatient"
import { useSelector } from "react-redux"
import FilterContainer from "@components/Common/Filters/FilterContainer"
import FullpageLoadingState from "@components/Common/FullpageLoadingState"

const ScheduleAppointmentPage = () => {
  const [appointmentStage, setAppointmentStage] = useState(0)
  const [appointmentDetails, setAppointmentDetails] = useState({
    duration: 60,
    location: "online",
    type: "consultation",
    note: "",
  })

  // Add filter states
  const [showFilters, setShowFilters] = useState(false)
  const [filterToBeExpanded, setFilterToBeExpanded] = useState(null)
  const [paginationState, setPaginationState] = useState({
    page: 1,
    limit: 10,
  })
  const [activeFilterField, setActiveFilterField] = useState(null)
  const [patientData, setPatientData] = useState([])

  const { handleGetPatient } = usePatient()
  const { patients, isGetPatientLoading } = useSelector(
    (state) => state.patient
  )

  // Initial data load when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      const initialParams = {
        page: 1,
        limit: 10,
      }
      const result = await handleGetPatient(initialParams)
      if (result && result.data) {
        setPatientData(result.data)
        // console.log("Initial patient data loaded:", result.data)
      }
    }

    fetchInitialData()
  }, [handleGetPatient])

  // Update data when filters change
  useEffect(() => {
    const fetchPatients = async () => {
      // Don't refetch if it's just the initial state
      if (Object.keys(paginationState).length > 2) {
        const result = await handleGetPatient(paginationState)
        if (result && result.data) {
          setPatientData(result.data)
          // console.log("Updated patient data:", result.data)
        }
      }
    }

    fetchPatients()
  }, [handleGetPatient, paginationState])

  // Define filter configurations - using patientData with correct paths
  const patientFilters = [
    {
      filterName: "First Name",
      filterProperty: "firstName",
      filterPath: "local.firstName",
      dataSource: patientData || [],
    },
    {
      filterName: "Last Name",
      filterProperty: "lastName",
      filterPath: "local.lastName",
      dataSource: patientData || [],
    },
    {
      filterName: "Email",
      filterProperty: "email",
      filterPath: "local.email",
      dataSource: patientData || [],
    },
    {
      filterName: "Contact",
      filterProperty: "contact",
      filterPath: "local.contact",
      dataSource: patientData || [],
    },
  ]

  return (
    <NavigationsPage>
      <PageWrapper className={styles.scheduleAppointmentPage}>
        <div className="overflow-hidden flex flex-col">
          <div className="hidden md:flex gap-2 mb-8">
            <ArrowLeftIcon />
            <span className="text-sm text-[--primary-blue] font-bold">
              BACK
            </span>
          </div>
          <button className="md:hidden mb-5">
            <ArrowSquareLeftIcon />
          </button>
          <div className="flex justify-between items-center mb-8">
            <div className="text-sm md:text-3xl font-bold text-[--primary-blue] md:text-[--quaternary-black] md:font-normal">
              Schedule Appointment
            </div>
          </div>
          <div className="flex justify-between mb-8">
            <AppointmentSectionHeader
              icon={<PatientInfoIcon />}
              title="Patient info"
              appointmentStage={appointmentStage}
            />
            <AppointmentSectionHeader
              icon={<MeetingInfoIcon />}
              title="Meeting info"
              appointmentStage={appointmentStage}
              className={`${appointmentStage >= 1 ? "border-[--primary-blue]" : "border-[#EFF0F0]"}`}
            />
            <AppointmentSectionHeader
              icon={<NotesIcon />}
              title="Notes"
              appointmentStage={appointmentStage}
              className={`${appointmentStage >= 2 ? "border-[--primary-blue]" : "border-[#EFF0F0]"}`}
            />
            <AppointmentSectionHeader
              icon={<ConfirmAndSendIcon />}
              title="Confirm and send"
              appointmentStage={appointmentStage}
              className={`${appointmentStage >= 3 ? "border-[--primary-blue]" : "border-[#EFF0F0]"}`}
            />
          </div>

          {appointmentStage === 0 ? (
            <PatientInfo
              setAppointmentStage={setAppointmentStage}
              appointmentDetails={appointmentDetails}
              setAppointmentDetails={setAppointmentDetails}
              setShowFilters={setShowFilters}
              setFilterToBeExpanded={setFilterToBeExpanded}
              setActiveFilterField={setActiveFilterField}
              activeFilterField={activeFilterField}
              patientData={patientData}
              paginationState={paginationState}
            />
          ) : null}

          {appointmentStage === 1 ? (
            <MeetingInfo
              setAppointmentStage={setAppointmentStage}
              appointmentDetails={appointmentDetails}
              setAppointmentDetails={setAppointmentDetails}
            />
          ) : null}

          {appointmentStage === 2 ? (
            <Notes
              setAppointmentStage={setAppointmentStage}
              setAppointmentDetails={setAppointmentDetails}
              appointmentDetails={appointmentDetails}
            />
          ) : null}

          {appointmentStage === 3 ? (
            <ConfirmAndSend
              appointmentDetails={appointmentDetails}
              setAppointmentStage={setAppointmentStage}
              setAppointmentDetails={setAppointmentDetails}
            />
          ) : null}
        </div>

        {showFilters && (
          <>
            <div
              className="fixed inset-0 z-[50] bg-transparent"
              onClick={() => setShowFilters(false)}
            />
            <FilterContainer
              setShowFilters={setShowFilters}
              filterToBeExpanded={filterToBeExpanded}
              setFilterToBeExpanded={setFilterToBeExpanded}
              setPaginationState={setPaginationState}
              paginationState={paginationState}
              filters={patientFilters}
              title="Patient Filters"
            />
          </>
        )}

        {isGetPatientLoading && <FullpageLoadingState />}
      </PageWrapper>
    </NavigationsPage>
  )
}

export default ScheduleAppointmentPage
